import React from 'react';
import useSeoTags from '../utils/useSeoTags';

/**
 * Terms of Service for the Funeral Professional (B2B) portal at
 * `/funeral-directors/terms`.
 *
 * This is intentionally separate from the consumer `/terms` page:
 * the legal relationship, acceptable use, billing model, and data
 * handling are all different when the customer is a funeral firm
 * using ObitAuthor as an internal tool for the families it serves.
 */
const FdTerms = () => {
  const updated = 'April 20, 2026';
  useSeoTags({
    title: 'Funeral Professional Portal — Terms of Service | ObitAuthor',
    description:
      'Terms of Service for funeral homes, cremation services, and other professional firms using the ObitAuthor Funeral Professional portal and its white-label obituary software.',
    canonical: 'https://obitauthor.com/funeral-directors/terms',
    robots: 'index, follow',
  });
  return (
    <div className="container py-5 fd-auth fd-legal">
      <div className="row justify-content-center">
        <div className="col-lg-10 col-xl-9">
          <div className="glass-card fd-legal-card">
            <span className="fd-auth-badge">For funeral professionals</span>
            <h1 className="fd-legal-title mt-3 mb-2">
              Funeral Professional Portal — Terms of Service
            </h1>
            <p className="fd-legal-updated">Last updated {updated}</p>

            <p>
              These Terms of Service (&ldquo;Terms&rdquo;) govern use of the
              Funeral Professional portal operated by ObitAuthor
              (&ldquo;ObitAuthor,&rdquo; &ldquo;we,&rdquo; or
              &ldquo;us&rdquo;) and accessed at
              <em> obitauthor.com/funeral-directors</em>. By creating an
              account or otherwise using the portal, the funeral home,
              cremation service, or other professional firm
              (&ldquo;Firm,&rdquo; &ldquo;you&rdquo;) agrees to these Terms.
              If you do not agree, do not use the portal.
            </p>

            <h2>1. The service</h2>
            <p>
              The portal is a credit-based web application that lets your
              staff draft, edit, preview, and download obituaries for the
              families your Firm serves, optionally branded with your
              Firm&rsquo;s name and logo. One AI-generated obituary
              consumes one credit; edits, redownloads, and manual
              obituaries do not.
            </p>

            <h2>2. Accounts and authorized users</h2>
            <p>
              You are responsible for maintaining the confidentiality of
              login credentials for your Firm&rsquo;s account and for all
              activity performed under those credentials. You may allow
              your employees and contractors to use the portal on your
              Firm&rsquo;s behalf; you remain responsible for their
              compliance with these Terms. Do not share credentials with
              anyone outside your Firm.
            </p>

            <h2>3. Free trial credits</h2>
            <p>
              New Firms receive an initial allotment of trial credits at
              no cost. Trial credits do not require billing information
              on file, are non-transferable, and have no cash value. We
              may change the trial size or limit it to one per Firm
              without notice.
            </p>

            <h2>4. Paid credits and billing</h2>
            <p>
              Additional credits are sold in prepaid bundles through our
              payment processor. Prices are shown at purchase. Unless
              required by law, prepaid credit bundles are
              <strong> non-refundable</strong> once purchased; they do
              not expire while your account is in good standing. If a
              charge is reversed or disputed, we may suspend the
              corresponding credits and/or your account until the
              dispute is resolved.
            </p>

            <h2>5. White-label branding</h2>
            <p>
              You may upload your Firm&rsquo;s name and logo for
              inclusion on generated PDFs. You represent that you own or
              have the right to use any name, logo, or other material
              you upload, and you grant ObitAuthor a non-exclusive
              license to use it solely to render your branded outputs.
              ObitAuthor&rsquo;s own marks may appear in
              non-family-facing surfaces such as administrative pages.
            </p>

            <h2>6. Content about decedents and families</h2>
            <p>
              Your staff or the families you serve may enter personal
              details about a decedent to generate an obituary. You
              represent that you have collected and are entitled to
              process that information under applicable law and your own
              engagement with the family. ObitAuthor processes that
              content solely to operate and improve the service and
              does not sell it. Generated obituaries are the
              family&rsquo;s tribute — they are not intended to be, and
              should not be treated as, an official record.
            </p>

            <h2>7. Acceptable use</h2>
            <ul>
              <li>
                Do not use the portal to impersonate another firm, to
                generate obituaries about living people without their
                consent, or to harass any person.
              </li>
              <li>
                Do not attempt to scrape, resell, or expose the portal as
                a standalone product or API to third parties outside
                your Firm.
              </li>
              <li>
                Do not use the portal for pets or other non-human
                decedents; that is not the service&rsquo;s intended use.
              </li>
              <li>
                Do not bypass credit metering, rate limits, or
                authentication controls.
              </li>
            </ul>

            <h2>8. AI-generated content disclaimer</h2>
            <p>
              Obituaries are produced by automated models from the
              inputs your staff supplies. Output may contain errors,
              omissions, or inappropriate phrasing and must be reviewed
              and approved by a qualified member of your staff before
              being published or delivered to a family. The portal
              provides an in-browser editor for exactly this purpose.
              ObitAuthor is not a publisher of the obituaries your
              Firm produces through the portal.
            </p>

            <h2>9. Availability and support</h2>
            <p>
              We use reasonable efforts to keep the portal available and
              to respond to support requests raised through the
              in-product Help page, but we do not offer a guaranteed
              uptime or dedicated support hours at this time. Planned
              maintenance may briefly interrupt service.
            </p>

            <h2>10. Intellectual property</h2>
            <p>
              The software, designs, copy, and non-customer-branded
              templates in the portal are owned by ObitAuthor or its
              licensors. You receive a limited, revocable,
              non-exclusive right to use the portal during your
              subscription to draft obituaries for families your Firm
              serves. The obituary content your staff finalizes is
              yours to deliver to the family.
            </p>

            <h2>11. Suspension and termination</h2>
            <p>
              We may suspend or terminate your account for breach of
              these Terms, for non-payment, or to protect the integrity
              of the portal. On termination, any unused paid credits
              tied to your account may be forfeited to the extent
              permitted by law. You may stop using the portal at any
              time; your saved obituaries remain accessible for export
              for a reasonable period after cancellation.
            </p>

            <h2>12. Disclaimers</h2>
            <p>
              The portal is provided on an &ldquo;as is&rdquo; and
              &ldquo;as available&rdquo; basis. ObitAuthor disclaims
              all warranties to the maximum extent permitted by law,
              including any implied warranties of merchantability,
              fitness for a particular purpose, and non-infringement.
            </p>

            <h2>13. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, ObitAuthor&rsquo;s
              aggregate liability arising out of or related to the
              portal will not exceed the amount your Firm paid to us in
              the three (3) months preceding the event giving rise to
              the claim. ObitAuthor will not be liable for indirect,
              incidental, special, consequential, or punitive damages,
              or for lost profits or revenues.
            </p>

            <h2>14. Indemnity</h2>
            <p>
              You will defend, indemnify, and hold harmless ObitAuthor
              from claims arising out of your Firm&rsquo;s use of the
              portal, your branding materials, or the content you or
              the families you serve submit into the portal.
            </p>

            <h2>15. Changes</h2>
            <p>
              We may update these Terms from time to time. When we do,
              we will post the revised version with a new
              &ldquo;Last updated&rdquo; date. If the changes are
              material, we will take reasonable steps to notify active
              Firms. Continued use of the portal after the effective
              date constitutes acceptance of the updated Terms.
            </p>

            <h2>16. Governing law</h2>
            <p>
              These Terms are governed by the laws of the State of
              Tennessee, United States, without regard to conflict of
              laws principles. The state and federal courts located in
              Tennessee will have exclusive jurisdiction over any
              dispute arising out of or related to these Terms.
            </p>

            <h2>17. Contact</h2>
            <p>
              Questions about these Terms can be submitted through the
              in-product Help &amp; contact page from your Firm&rsquo;s
              dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FdTerms;
